// src/pages/dossier-ia-recrutement.tsx
//
// Page d'accès réservé (gated content) après capture de lead via la modale
// IADossierModal. Vérifie le localStorage flag au mount — si absent,
// redirige vers /entreprise/ia. Page noindex (contenu exclusif).

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  Search,
  Filter,
  ClipboardCheck,
  Workflow,
  AlertTriangle,
  Compass,
  Brain,
  Lock,
  ExternalLink,
  ArrowRight,
  BookOpen,
} from 'lucide-react';
import Layout from '../components/layout/layout';
import Section from '../components/ui/section';
import SectionBackground from '../components/sections/section-background';

const STORAGE_KEY = 'unlocked:dossier-ia-recrutement';

const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

const h2Class =
  'text-3xl md:text-4xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';

const h3Class = 'text-xl font-semibold text-[#30475e] mb-3 leading-snug';

const bodyClass = 'text-base text-neutral-700 leading-[1.85]';

// — Sections principales (apports de l'IA) —
const APPORTS = [
  {
    icon: Search,
    eyebrow: 'Apport 1',
    title: 'Sourcing',
    intro:
      "L'IA accélère la phase d'identification des candidats — la plus chronophage et la moins différenciante du recrutement.",
    points: [
      {
        h: 'Recherche sémantique',
        p: "Au lieu de chercher des mots-clés exacts ('Java 8 ans Spring Boot'), les modèles d'embeddings comprennent l'équivalence métier ('développeur backend expérimenté JVM'). Le vivier identifié devient 3 à 5× plus large à compétence équivalente.",
      },
      {
        h: 'Élargissement des profils transférables',
        p: "Identification de profils qui n'ont pas exactement le bon titre mais qui ont 80 % des compétences transférables — par exemple un commercial pharma qui peut basculer vers la medtech.",
      },
      {
        h: 'Approche directe automatisée',
        p: "Personnalisation à l'échelle des messages d'approche LinkedIn ou email, calibrés sur le profil exact du candidat (parcours, langue, signaux récents). Taux de réponse multiplié par 2 à 3 vs. les messages templates classiques.",
      },
    ],
    sources: [
      {
        label: 'LinkedIn Future of Recruiting Report 2024',
        url: 'https://business.linkedin.com/talent-solutions/resources/future-of-recruiting',
      },
      {
        label: 'McKinsey · The state of AI in 2024',
        url: 'https://www.mckinsey.com/capabilities/quantumblack/our-insights/the-state-of-ai',
      },
    ],
  },
  {
    icon: Filter,
    eyebrow: 'Apport 2',
    title: 'Sélection',
    intro:
      "L'IA pré-trie et classe les CV reçus selon leur adéquation au poste — sans remplacer la décision finale du recruteur.",
    points: [
      {
        h: 'Scoring sémantique de CV',
        p: "Plutôt qu'un filtrage par mots-clés ATS (qui rejette 75 % des CV pertinents pour cause de formulation différente), les modèles attribuent un score de match métier. Réduction du temps de tri de 60 à 80 % selon Gartner.",
      },
      {
        h: 'Détection de signaux faibles',
        p: "Stabilité de parcours, vitesse de progression, cohérence des contextes (taille d'entreprise, secteur). L'IA structure ces signaux que le recruteur humain a tendance à survoler quand il lit 100 CV à la suite.",
      },
      {
        h: 'Premier filtre objectif anti-biais',
        p: "Bien calibrée, l'IA peut être plus objective qu'un recruteur fatigué : pas d'effet 'CV consonance', pas d'effet 'photo'. À condition d'auditer les données d'entraînement (cf. limites plus bas).",
      },
    ],
    sources: [
      {
        label: 'Gartner · HR Technology Survey 2024',
        url: 'https://www.gartner.com/en/human-resources/research/talent-management',
      },
      {
        label: 'Harvard Business Review · How AI Affects Hiring',
        url: 'https://hbr.org/topic/subject/hiring-and-recruitment',
      },
    ],
  },
  {
    icon: ClipboardCheck,
    eyebrow: 'Apport 3',
    title: 'Évaluation',
    intro:
      "L'IA outille l'évaluation des compétences techniques et comportementales — sans se substituer au jugement humain.",
    points: [
      {
        h: 'Tests adaptatifs',
        p: "Algorithmes qui adaptent la difficulté en temps réel selon les réponses du candidat. Mesure plus fine du niveau réel qu'un test linéaire classique. Réduction du temps d'évaluation moyen de 35 %.",
      },
      {
        h: 'Analyse de vidéo d\'entretien',
        p: "Transcription automatique, extraction des points-clés, signaux verbaux. Attention : l'analyse non-verbale (émotions, micro-expressions) est techniquement disponible mais ÉTHIQUEMENT et JURIDIQUEMENT problématique (cf. EU AI Act).",
      },
      {
        h: 'Études de cas IA',
        p: "Génération automatique d'études de cas personnalisées (mise en situation métier réelle, sourcée du contexte client). Le candidat est évalué sur son raisonnement, pas sa préparation à des questions standardisées.",
      },
    ],
    sources: [
      {
        label: 'EU AI Act · Recruitment as High-Risk (Annex III)',
        url: 'https://artificialintelligenceact.eu/annex/3/',
      },
      {
        label: 'NIST · AI Risk Management Framework',
        url: 'https://www.nist.gov/itl/ai-risk-management-framework',
      },
    ],
  },
  {
    icon: Workflow,
    eyebrow: 'Apport 4',
    title: 'Automatisation des process',
    intro:
      "L'IA absorbe les tâches répétitives à faible valeur ajoutée — planning, communication, suivi, reporting.",
    points: [
      {
        h: 'Planification d\'entretiens',
        p: "Assistants IA qui dialoguent avec les candidats et l'équipe pour caler les créneaux, gérer les replanifications, envoyer les rappels. Économie estimée 4-6h/semaine pour un recruteur traitant 20 candidats actifs.",
      },
      {
        h: 'Communication candidat',
        p: "Réponses personnalisées aux candidatures (pas de 'votre candidature a été reçue' générique). Suivi proactif aux étapes clés. Mesurablement : satisfaction candidat +25 points selon Talent Board.",
      },
      {
        h: 'Reporting & pilotage',
        p: "Tableaux de bord générés automatiquement : sources les plus performantes, durée par étape, taux de conversion, biais détectés. La donnée que tout le monde a, mais que personne n'exploite, devient enfin lisible.",
      },
    ],
    sources: [
      {
        label: 'Talent Board · Candidate Experience Research Report',
        url: 'https://www.thetalentboard.org/benchmark-research/',
      },
      {
        label: 'SHRM · State of the Workplace Report',
        url: 'https://www.shrm.org/topics-tools/research',
      },
    ],
  },
];

// — Limites —
const LIMITES = [
  {
    icon: Brain,
    title: 'La connaissance métier',
    intro:
      "L'IA ne sait pas ce qu'est un bon profil dans VOTRE contexte — elle apprend ce qu'elle a vu, pas ce qui marche.",
    points: [
      "Un modèle entraîné sur 100 000 CV du secteur tech ne sait pas qu'un VRP retail dans la région PACA a un profil culturel et commercial très différent d'un VRP tech à Paris.",
      "Les signaux 'parcours atypique' que l'IA marque comme négatifs sont souvent les meilleurs profils sur des postes pivots — exactement ceux que le recruteur senior aurait repérés.",
      "L'IA généralise ; un bon recrutement individualise. Sur un poste critique, le 'cas Edge' est presque toujours plus précieux que le 'cas moyen' que l'IA promeut.",
    ],
    sources: [
      {
        label: 'MIT Sloan · The Limits of Algorithmic Hiring',
        url: 'https://sloanreview.mit.edu/topic/talent-and-leadership/',
      },
    ],
  },
  {
    icon: Compass,
    title: 'Le jugement',
    intro:
      "L'IA optimise sur ce qu'elle peut mesurer. Le recrutement, lui, dépend en grande partie de ce qui ne se mesure pas.",
    points: [
      "Compatibilité humaine avec le manager direct : 0 % mesurable par IA, ~70 % du succès d'une intégration selon Gallup.",
      "Capacité de leadership en situation d'incertitude : non testable hors mise en situation réelle, souvent observable seulement à 6-12 mois.",
      "Honnêteté et intégrité : aucun modèle ne les détecte ; seule la confrontation avec des questions ouvertes par un recruteur expérimenté permet de les approcher.",
      "Adéquation à la culture d'entreprise : nécessite une compréhension fine du contexte client que seul un humain qui a passé du temps sur place possède réellement.",
    ],
    sources: [
      {
        label: 'Gallup · Employee Engagement Reports',
        url: 'https://www.gallup.com/workplace/topic/employee-engagement.aspx',
      },
      {
        label: 'APEC · Études Cadres',
        url: 'https://corporate.apec.fr/home/nos-etudes.html',
      },
    ],
  },
  {
    icon: AlertTriangle,
    title: 'Les biais hérités',
    intro:
      "L'IA reproduit (et amplifie) les biais des données sur lesquelles elle a été entraînée.",
    points: [
      "Amazon a dû abandonner en 2018 son outil IA de recrutement parce qu'il discriminait systématiquement les CV féminins sur les postes tech — il avait appris des décisions historiques masculines.",
      "Les modèles open-source pré-entraînés (CV scoring) reproduisent les biais de représentativité des datasets utilisés (souvent surreprésentation de profils anglo-saxons, urbains, jeunes).",
      "L'audit annuel des biais devient une obligation légale en Europe avec l'EU AI Act (catégorie 'high-risk' pour le recrutement, applicable à partir d'août 2026).",
    ],
    sources: [
      {
        label: 'Reuters · Amazon scraps secret AI recruiting tool',
        url: 'https://www.reuters.com/article/world/insight-amazon-scraps-secret-ai-recruiting-tool-that-showed-bias-against-women-idUSKCN1MK0AG/',
      },
      {
        label: 'EU AI Act · Texte officiel',
        url: 'https://artificialintelligenceact.eu/',
      },
    ],
  },
];

export default function DossierIaRecrutementPage() {
  const navigate = useNavigate();
  const [isUnlocked, setIsUnlocked] = useState<boolean | null>(null);

  useEffect(() => {
    // Check localStorage flag — si pas unlocked, redirige
    try {
      const unlocked = localStorage.getItem(STORAGE_KEY);
      if (unlocked === '1') {
        setIsUnlocked(true);
      } else {
        setIsUnlocked(false);
        // Petit délai puis redirige (UX : on ne flash pas la page)
        setTimeout(() => navigate('/entreprise/ia', { replace: true }), 100);
      }
    } catch {
      setIsUnlocked(false);
      navigate('/entreprise/ia', { replace: true });
    }
  }, [navigate]);

  if (isUnlocked === null) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse text-neutral-400">Vérification…</div>
        </div>
      </Layout>
    );
  }

  if (isUnlocked === false) {
    return (
      <Layout>
        <Helmet>
          <title>Accès restreint | recruteurs.io</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <Lock className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#30475e] mb-3">
              Accès réservé
            </h1>
            <p className="text-neutral-600 mb-6">
              Ce dossier est accessible après inscription depuis la page IA RH
              sur-mesure. Redirection en cours…
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title="Dossier IA & Recrutement — Apports, limites, sources | recruteurs.io"
      description="Dossier exclusif : ce que l'IA apporte vraiment au recrutement (sourcing, sélection, évaluation, automatisation) et ses limites (jugement, connaissance métier, biais). Sources : LinkedIn, Gartner, MIT, EU AI Act, APEC."
    >
      <Helmet>
        {/* Page gatée : noindex */}
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* HERO */}
      <SectionBackground
        page="dossier-ia"
        section="d-hero"
        className="relative overflow-hidden bg-gradient-to-br from-[#fff1f2] via-[#fef9c3] to-[#e0f2ff]"
      >
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
        >
          <div className="absolute -top-32 -left-32 w-96 h-96 bg-[#d03484]/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-[#d47254]/10 rounded-full blur-3xl" />
        </div>

        <section className="relative z-10 py-16 md:py-20">
          <div className="container-custom max-w-4xl mx-auto text-center">
            <span className={eyebrowClass}>
              <Lock className="w-3.5 h-3.5 mr-2 text-[#d03484]" />
              Accès privé · Merci pour votre inscription
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-[3.5rem] font-extrabold tracking-tight leading-[1.1] pb-1 mb-5">
              <span className="text-[#30475e]">L'IA dans le recrutement&nbsp;: </span>
              <span className={gradientText}>
                ce qu'elle apporte vraiment, et où elle s'arrête.
              </span>
            </h1>
            <p className="text-base md:text-lg text-neutral-700 leading-relaxed">
              Quatre apports concrets de l'IA appliquée au recrutement — sourcing,
              sélection, évaluation, automatisation — et trois limites
              structurelles qu'aucun modèle ne franchira jamais sans expertise
              humaine. Sources citées, vérifiables.
            </p>
          </div>
        </section>
      </SectionBackground>

      {/* SECTION — 4 APPORTS */}
      <SectionBackground page="dossier-ia" section="d-apports" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Les 4 apports concrets</span>
            <h2 className={h2Class}>
              Là où l'IA{' '}
              <span className={gradientText}>change vraiment les choses.</span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-10">
            {APPORTS.map((apport, idx) => {
              const Icon = apport.icon;
              return (
                <motion.div
                  key={apport.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: idx * 0.05 }}
                  className="rounded-[1.75rem] bg-white ring-1 ring-neutral-200 shadow-sm p-7 md:p-10"
                >
                  <div className="flex items-start gap-4 mb-6 pb-5 border-b border-neutral-100">
                    <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center shadow-md shadow-[#d03484]/20">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-widest text-[#d03484] bg-[#fde7f2]/60 rounded-full px-2.5 py-0.5 mb-2">
                        {apport.eyebrow}
                      </span>
                      <h3 className="text-2xl font-bold text-[#30475e] mb-2 leading-snug">
                        {apport.title}
                      </h3>
                      <p className={bodyClass}>{apport.intro}</p>
                    </div>
                  </div>

                  <div className="space-y-5">
                    {apport.points.map((pt, i) => (
                      <div key={i}>
                        <h4 className={h3Class + ' text-base'}>{pt.h}</h4>
                        <p className={bodyClass + ' text-sm'}>{pt.p}</p>
                      </div>
                    ))}
                  </div>

                  {/* Sources */}
                  <div className="mt-6 pt-5 border-t border-neutral-100">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 mb-2">
                      Sources
                    </p>
                    <ul className="space-y-1.5">
                      {apport.sources.map((src, i) => (
                        <li key={i}>
                          <a
                            href={src.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs text-[#d03484] hover:underline"
                          >
                            <BookOpen className="w-3 h-3" />
                            {src.label}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION — LIMITES */}
      <SectionBackground page="dossier-ia" section="d-limites" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Les 3 limites structurelles</span>
            <h2 className={h2Class}>
              Là où l'IA{' '}
              <span className={gradientText}>ne remplacera jamais l'humain.</span>
            </h2>
            <p className="text-lg text-neutral-600 leading-relaxed">
              Toute conversation sur l'IA dans le recrutement doit inclure cette
              partie. Les cabinets qui vous vendent de l'IA «&nbsp;100 %&nbsp;»
              vous vendent un échec contractuel à 18 mois.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            {LIMITES.map((lim, idx) => {
              const Icon = lim.icon;
              return (
                <motion.div
                  key={lim.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: idx * 0.05 }}
                  className="rounded-[1.75rem] bg-white ring-1 ring-amber-200 shadow-sm p-7 md:p-10"
                >
                  <div className="flex items-start gap-4 mb-5">
                    <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-amber-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-widest text-amber-700 bg-amber-50 rounded-full px-2.5 py-0.5 mb-2">
                        Limite {idx + 1}
                      </span>
                      <h3 className="text-2xl font-bold text-[#30475e] mb-2 leading-snug">
                        {lim.title}
                      </h3>
                      <p className={bodyClass}>{lim.intro}</p>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-5">
                    {lim.points.map((p, i) => (
                      <li key={i} className="flex gap-2.5 text-sm text-neutral-700 leading-relaxed">
                        <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-amber-500" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Sources */}
                  <div className="pt-4 border-t border-neutral-100">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 mb-2">
                      Sources
                    </p>
                    <ul className="space-y-1.5">
                      {lim.sources.map((src, i) => (
                        <li key={i}>
                          <a
                            href={src.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs text-[#d03484] hover:underline"
                          >
                            <BookOpen className="w-3 h-3" />
                            {src.label}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION — NOTRE POSITIONNEMENT */}
      <SectionBackground page="dossier-ia" section="d-positioning" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto rounded-[2rem] bg-gradient-to-br from-[#30475e] to-[#1f3245] text-white p-10 md:p-14 shadow-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur-sm px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-[#fde7f2] mb-5">
              Notre positionnement
            </span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-5 leading-[1.1]">
              recruteurs.io : l'IA{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#fde7f2] to-[#fde2d3]">
                au service du jugement humain.
              </span>
            </h2>
            <p className="text-base md:text-lg text-white/85 leading-relaxed mb-6">
              Nous avons développé en interne <strong>trois outils IA propriétaires</strong>{' '}
              (Mapping RH, scoring sémantique de CV, génératif marque employeur)
              que nous utilisons sur chaque mission. Mais aucune décision de
              recrutement n'est jamais prise par un algorithme.
            </p>
            <p className="text-base md:text-lg text-white/85 leading-relaxed mb-8">
              L'IA fait le tri, accélère le sourcing, structure la donnée.
              <strong> Un consultant humain expérimenté</strong>{' '}
              ré-évalue chaque profil, mène les entretiens, juge l'adéquation
              culturelle, et porte la responsabilité de la recommandation finale.
              Ce duo est notre vrai différenciateur.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/reservation')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-2.5 text-sm font-bold text-[#30475e] hover:bg-[#fde7f2] hover:-translate-y-0.5 transition-all shadow-lg whitespace-nowrap"
              >
                Cadrer un projet IA RH
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/entreprise/ia')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl ring-1 ring-white/30 bg-white/10 backdrop-blur-sm px-6 py-2.5 text-sm font-semibold text-white hover:bg-white/20 transition-all whitespace-nowrap"
              >
                Voir notre offre IA RH sur-mesure
              </button>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* BIBLIOGRAPHIE COMPLETE */}
      <SectionBackground page="dossier-ia" section="d-biblio" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className={eyebrowClass}>Bibliographie</span>
              <h2 className={h2Class}>
                Sources <span className={gradientText}>vérifiables</span> citées dans ce dossier.
              </h2>
              <p className={bodyClass}>
                Toutes les références utilisées, accessibles publiquement,
                regroupées en une liste. Si vous trouvez un lien cassé, signalez-le
                à contact@recruteurs.io.
              </p>
            </div>

            <ul className="space-y-3">
              {[
                ...APPORTS.flatMap((a) => a.sources),
                ...LIMITES.flatMap((l) => l.sources),
              ]
                .filter(
                  (s, i, arr) => arr.findIndex((x) => x.url === s.url) === i,
                )
                .map((src, i) => (
                  <li
                    key={i}
                    className="rounded-xl bg-white ring-1 ring-neutral-200 px-4 py-3"
                  >
                    <a
                      href={src.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-[#30475e] hover:text-[#d03484] transition-colors"
                    >
                      <BookOpen className="w-4 h-4 text-[#d03484] flex-shrink-0" />
                      <span className="font-medium">{src.label}</span>
                      <ExternalLink className="w-3.5 h-3.5 text-neutral-400 ml-auto flex-shrink-0" />
                    </a>
                  </li>
                ))}
            </ul>
          </div>
        </Section>
      </SectionBackground>
    </Layout>
  );
}

// Export du flag localStorage pour réutilisation depuis la modale qui débloque
export const DOSSIER_IA_STORAGE_KEY = STORAGE_KEY;
